/*
 *  -----------------------------------------------------------------------
 *    <copyright file="panel-display.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 */

import HighchartsReact from 'highcharts-react-official';
import React, { RefObject, useEffect, useState } from 'react';
import { useLocalState, useWindowSize } from 'hooks';
import { FormatNumberForChart, HC_COLORS_STANDARD, OpraColours } from 'interfaces/opra-chart.interface';
import { FieldTag, PanelType } from 'types/graphql-global-types';
import PanelConstants from 'views/components/central-monitoring/panels/add-panel/panel-constants';
import { getColumn } from 'views/components/central-monitoring/panels/column/display';
import { getDonut } from 'views/components/central-monitoring/panels/donut/display';
import { getFunnel, getFunnelTrend } from 'views/components/central-monitoring/panels/funnel/display';
import { getHeatmap } from 'views/components/central-monitoring/panels/heatmap/display';
import { getLine } from 'views/components/central-monitoring/panels/line/display';
import { getLineByCategory } from 'views/components/central-monitoring/panels/line/display-by-category';
import { getRateGroup, getRateGroupTrend } from 'views/components/central-monitoring/panels/rate/display';
import { getStackedColumn } from 'views/components/central-monitoring/panels/stacked-column/display';
import { getTriOperational, getTriOperationalTrend } from 'views/components/central-monitoring/panels/tri/display';

export interface IPanelDisplayData {
    type: PanelType;
    modelConfig: any;
    chartConfig: any;
    panelData: any;
    hidePlotTitle?: boolean;
    setRef?: (ref: RefObject<HighchartsReact.RefObject> | null) => void;
    setLoadingComplete?: () => void;
    panelFunctions?: () => void;
    entityName?: string;
    siteId?: string;
    hideLegend?: boolean;
    previewMode?: boolean;
    dashboardType?: string;
}

type SecondaryLegendItem = {
    label: string;
    colour: OpraColours;
    value?: number;
    sectionPrefix?: string;
};

const PanelDisplay = (panelProps: IPanelDisplayData) => {
    const size = useWindowSize();
    const [panelState, setPanel] = useState<IPanelDisplayData | null>(null);
    const [stateStudyData, setStateStudyData] = useState<{ sites: { Fields: Record<string, any> }[] } | null>(null);
    const { state } = useLocalState();
    useEffect(() => setStateStudyData(state.studyData), [state.studyData]);
    useEffect(() => {
        if (JSON.stringify(panelProps) !== JSON.stringify(panelState)) setPanel(panelProps);
    }, [panelProps]);
    const { siteId, setLoadingComplete } = panelProps;

    if (panelState === null) {
        if (setLoadingComplete) setLoadingComplete();
        return null;
    }

    if (panelState.panelData?.panelData?.length && panelState.panelData.panelData[0].error) {
        if (setLoadingComplete) setLoadingComplete();

        return <span>{panelState.panelData.panelData[0].error}</span>;
    }

    try {
        const config = state?.scoringData?.config ?? state?.studyData?.config;
        const sitesStatusesToExclude = !panelProps.previewMode ? config?.sitesStatusesToExclude : [];
        const sites =
            stateStudyData?.sites
                ?.filter((item) => sitesStatusesToExclude.indexOf(item.Fields['Site.status']) < 0)
                .reduce(
                    (acc, cur) => ({
                        ...acc,
                        [cur?.Fields['Site.id']]: cur,
                    }),
                    {},
                ) || {};
        const siteValues: any = Object.values(sites);

        /*  -- This is pretty slow
        const subjects = siteValues
            .flatMap((site: any) => site.Subjects)
            .reduce((acc: any, cur: any) => ({ ...acc, [cur?.Fields['Subject.id']]: cur }), {});
        */

        // While loops are fastest
        const subjects: any = {};
        const numSites = siteValues.length;
        let i = 0;
        while (i < numSites) {
            const numSubjects = siteValues[i].Subjects.length;
            let j = 0;
            while (j < numSubjects) {
                const subject = siteValues[i].Subjects[j];
                const id = subject.Fields['Subject.id'];
                if (id)
                    subjects[id] = {
                        Fields: {
                            'Subject.id': id,
                            'Subject.code': subject.Fields['Subject.code'],
                            'Subject.subjectNumber': subject.Fields['Subject.subjectNumber'],
                        },
                    };
                j++;
            }
            i++;
        }

        const sortField = panelState?.panelData?.panelData[0]?.sortField;

        const isSortFieldSubjectPopulation =
            sortField === undefined
                ? undefined
                : state.fields.find((f: any) => f.key === sortField)?.tags?.includes(FieldTag.SUBJECT_POPULATION);

        switch (panelState.type?.toUpperCase()) {
            case PanelType.GRAPH:
                switch (panelState.chartConfig.type?.toUpperCase()) {
                    case PanelConstants.graphTypes.HEATMAP:
                        return getHeatmap({
                            getRef: panelProps.setRef,
                            panelState,
                            siteId,
                            sites,
                            subjects,
                            setLoadingComplete,
                        });

                    case PanelConstants.graphTypes.COLUMN:
                        return getColumn({
                            getRef: panelProps.setRef,
                            panelState,
                            study: state.selectedStudy,
                            siteId,
                            sites,
                            setLoadingComplete,
                            isSortFieldSubjectPopulation,
                        });

                    case PanelConstants.graphTypes.SCATTER_PLOT_LINE:
                    case PanelConstants.graphTypes.SCATTERPLOTLINE: {
                        if (
                            panelState.chartConfig.seriesType === PanelConstants.seriesType.SUBJECT_BY_VISIT ||
                            panelState.chartConfig.seriesType === PanelConstants.seriesType.SUBJECTBYVISIT
                        ) {
                            return getLineByCategory({
                                getRef: panelProps.setRef,
                                panelState,
                                study: state.selectedStudy,
                                siteId,
                                sites,
                                setLoadingComplete,
                                subjects,
                                previewMode: panelProps.previewMode,
                            });
                        }
                        return getLine({
                            getRef: panelProps.setRef,
                            panelState,
                            sites,
                            siteValues,
                            subjects,
                            siteId,
                            setLoadingComplete,
                            previewMode: panelProps.previewMode,
                        });
                    }

                    case PanelConstants.graphTypes.DONUT:
                        return getDonut({
                            getRef: panelProps.setRef,
                            panelState,
                            siteId,
                        });

                    case PanelConstants.graphTypes.STACKEDCOLUMN:
                    case PanelConstants.graphTypes.STACKED_COLUMN:
                        return getStackedColumn({
                            getRef: panelProps.setRef,
                            panelState,
                            siteId,
                            sites,
                            subjects,
                        });

                    default:
                        if (setLoadingComplete) setLoadingComplete();

                        return (
                            <h3 className="error-message">
                                {panelState.chartConfig.type} charts are not yet available
                            </h3>
                        );
                }
            case PanelType.MODEL:
                if (!siteValues.length) {
                    if (setLoadingComplete) setLoadingComplete();

                    return <h3 className="error-message">There is no data available for this chart</h3>;
                }

                switch (panelState.modelConfig.type.toUpperCase()) {
                    case PanelConstants.modelOptions.TRI_OPERATIONAL:
                    case PanelConstants.modelOptions.TRIOPERATIONAL:
                        return getTriOperational({ getRef: panelProps.setRef, panelState, sites, siteId, size });

                    case PanelConstants.modelOptions.TRI_OPERATIONAL_TREND:
                    case PanelConstants.modelOptions.TRIOPERATIONALTREND:
                        return getTriOperationalTrend({
                            getRef: panelProps.setRef,
                            panelState,
                            sites,
                            siteId,
                            previewMode: panelProps.previewMode,
                        });

                    case PanelConstants.modelOptions.FUNNEL:
                        return getFunnel({ getRef: panelProps.setRef, panelState, sites, siteId, setLoadingComplete });

                    case PanelConstants.modelOptions.FUNNEL_TREND:
                    case PanelConstants.modelOptions.FUNNELTREND:
                        return getFunnelTrend({
                            getRef: panelProps.setRef,
                            panelState,
                            sites,
                            siteId,
                            study: state.selectedStudy,
                            setLoadingComplete,
                        });

                    case PanelConstants.modelOptions.RATE_GROUP:
                    case PanelConstants.modelOptions.RATEGROUP:
                        return getRateGroup({
                            getRef: panelProps.setRef,
                            panelState,
                            sites,
                            siteId,
                            setLoadingComplete,
                        });

                    case PanelConstants.modelOptions.RATE_GROUP_TREND:
                    case PanelConstants.modelOptions.RATEGROUPTREND:
                        return getRateGroupTrend({
                            getRef: panelProps.setRef,
                            panelState,
                            sites,
                            siteId,
                            study: state.selectedStudy,
                            previewMode: panelProps.previewMode,
                            setLoadingComplete,
                            dashboardType: panelProps.dashboardType,
                        });

                    default:
                        if (setLoadingComplete) setLoadingComplete();

                        return <h3 className="error-message">{panelState.modelConfig.type} is not yet available</h3>;
                }

            default:
                if (setLoadingComplete) setLoadingComplete();

                return <h3 className="error-message">Invalid panel type: {panelState.type}</h3>;
        }
    } catch (err: any) {
        console.log('Error loading panel:', panelState, err);

        return <h3 className="error-message">Error loading panel: {err.message}</h3>;
    }
};

export default React.memo(PanelDisplay);

const getThresholdLabel = (thresholdType: string, value?: number) => {
    const typeIdx: number = [
        PanelConstants.lineThresholdType.STUDYSPECIFIC,
        PanelConstants.lineThresholdType.STUDYMEAN,
        PanelConstants.lineThresholdType.STUDYMEDIAN,
        PanelConstants.lineThresholdType.SAMPLEMEAN,
        PanelConstants.lineThresholdType.SAMPLEMEDIAN,
    ].indexOf(thresholdType.replace('_', ''));

    if (typeIdx === -1) return `${thresholdType}${value ? ` ${FormatNumberForChart(value)}` : ''}`;

    const typeLabel = ['Study Specific', 'Study Mean', 'Study Median', 'Sample Mean', 'Sample Median'][typeIdx];

    const valLabel = value === null || value === undefined ? '' : `: ${FormatNumberForChart(value)}`;
    return `${typeLabel}${valLabel}`;
};

const formatKey = (items: SecondaryLegendItem[][], showAdditionalRow = false) => {
    const rows: string[] = items.map((itemRow) =>
        itemRow
            .map(
                (item) =>
                    `${item.sectionPrefix ? `${item.sectionPrefix}: ` : ''}` +
                    `<span style="font-size: large; color: ${HC_COLORS_STANDARD[item.colour]};">` +
                    `-</span> ${getThresholdLabel(item.label, item.value)}`,
            )
            .join(' '),
    );

    const additionalRows = `<div style="display: block">
            <span style="display: inline-flex; align-items: center;">
                <span style="color: ${HC_COLORS_STANDARD[1]}; font-size: 11px; margin-right: 3px;">✺</span>
                Severe
            </span>
            <span style="display: inline-flex; align-items: center;">
                <span style="color: ${HC_COLORS_STANDARD[2]}; font-size: 11px; margin-right: 3px;">✺</span>
                Moderate
            </span>
            <span style="display: inline-flex; align-items: center;">
                <span style="color:${HC_COLORS_STANDARD[3]}; font-size: 11px; margin-right: 3px;">✺</span>
                Normal
            </span>
        </div><div style="display: block">\n</div>`;

    return `<div style="text-align: center; width: 100%">
        ${showAdditionalRow ? additionalRows : ''} ${rows.join('<div display="block;"> </div>')}
    </div>`;
};

export { formatKey };
